import { observer } from 'mobx-react-lite'
import { useNavigate, useNavigation, useOutletContext } from 'react-router'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import {
  CardItemContent,
  CardList,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import {
  WizardBottomBarPrimaryButton,
  WizardContentBody,
  WizardContentHeader,
  WizardContentHeaderDescription,
  WizardContentHeaderTitle,
  WizardPageAnimatedContent,
  WizardPageSingleColumnRow,
} from '@/components/Wizard'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { links } from '@/utils/link'
import { MeasureOneExperience } from '@/features/jobs/details/tax-type/info-gathering/measure-one/mappers'
import styles from '../MeasureOne.module.css'
import { MeasureOneKicker } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneKicker'
import { MeasureOneSpinner } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneSpinner'
import { useEffect, useState } from 'react'
import { MeasureOneWizardBottomBar } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneWizardBottomBar'

const accountTypes = [
  {
    title: 'Tax documents',
    description: 'Pull available tax form directly from connected providers',
    experience: MeasureOneExperience.Employment,
    testId: 'tax-documents-experience-option',
  },
  {
    title: 'Bank documents',
    description:
      'Pull available bank statements directly from your financial accounts',
    experience: MeasureOneExperience.BankBrokerage,
    testId: 'bank-documents-experience-option',
  },
]

export default observer(function MeasureOneSelectAccountTypeContainer() {
  const navigate = useNavigate()
  const navigation = useNavigation()

  // Exception: this component uses state to track a temporary
  // "loading + navigating" pending state to avoid UI flicking on the experiences
  // options, we should prefer router navigation state but that didn't cleanly
  // cover this.
  const [isNavigating, setIsNavigating] = useState(false)

  const vm = useOutletContext<JobDetailsViewModel>()
  const taxVm = vm.typeVm as TaxJobDetailsViewModel
  const measureOneVm = taxVm.infoGatheringVm.measureOneVm

  useEffect(() => {
    if (navigation.state === 'idle') {
      setIsNavigating(false)
    }
  }, [navigation.state])

  const onPress = async (experience: MeasureOneExperience) => {
    setIsNavigating(true)

    try {
      await measureOneVm.initializeMeasureOneImportFlow(experience)
      await navigate(links.measureOneSelectConnection(vm.job.id))
    } catch {
      setIsNavigating(false)
    }
  }

  return (
    <>
      <WizardPageSingleColumnRow>
        <WizardPageAnimatedContent
          animationKey="default"
          animationDirection="forward"
        >
          <WizardContentHeader>
            <ExperienceTypeHeader />
          </WizardContentHeader>
        </WizardPageAnimatedContent>
      </WizardPageSingleColumnRow>
      <WizardPageSingleColumnRow>
        <WizardPageAnimatedContent
          animationKey="default"
          animationDirection="forward"
        >
          <WizardContentBody>
            {measureOneVm.initializeMeasureOneImportFlow.pending ||
            isNavigating ? (
              <MeasureOneSpinner />
            ) : (
              <CardList aria-label="Account types">
                {accountTypes.map(
                  ({ title, description, experience, testId }) => (
                    <MeasureOneAccountItem
                      loading={
                        measureOneVm.initializeMeasureOneImportFlow.pending
                      }
                      title={title}
                      description={description}
                      action={() => onPress(experience)}
                      key={title}
                      testId={testId}
                    />
                  ),
                )}
              </CardList>
            )}
          </WizardContentBody>
        </WizardPageAnimatedContent>
      </WizardPageSingleColumnRow>

      {measureOneVm.canShowBottomBar && (
        <MeasureOneWizardBottomBar>
          <WizardBottomBarPrimaryButton
            isPending={!measureOneVm.canNavigateToImportScreen}
            isDisabled={!measureOneVm.canNavigateToImportScreen}
            to={links.measureOneSelectDocuments(vm.job.id)}
            label="Select my documents"
          />
        </MeasureOneWizardBottomBar>
      )}
    </>
  )
})

function ExperienceTypeHeader() {
  return (
    <div className={styles.content} data-test="measure-one-experiences-header">
      <MeasureOneKicker />

      <WizardContentHeaderTitle>
        Select your account type
      </WizardContentHeaderTitle>
      <WizardContentHeaderDescription>
        Choose the type of account you want to connect through MeasureOne.
      </WizardContentHeaderDescription>
    </div>
  )
}

interface MeasureOneAccountItemProps {
  title: string
  description: string
  action: () => unknown
  loading: boolean
  testId?: string
}

function MeasureOneAccountItem({
  title,
  description,
  action,
  loading,
  testId,
}: MeasureOneAccountItemProps) {
  return (
    <CardListItem testId={testId} isDisabled={loading} onAction={action}>
      <CardItemContent align="center" isInteractive>
        <CardTitle>{title}</CardTitle>
        <CardSubtitle>{description}</CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}
